import sharedSupabase from '../config/supabase';

/**
 * The writer for "what package was this location counted in last time".
 *
 * `count_template_items.count_last_used_purchase_option_id` /
 * `count_last_used_at` (migration 20260801120000) are read by the
 * count-presentation resolver as its "last used for this location" rung, but
 * until this service existed nothing ever stamped them — the rung only fired
 * where a row already carried values, i.e. never. This runs when a count
 * session completes, beside applyInferredScheduleOnComplete, from every path
 * that writes `status: 'completed'`.
 *
 * What counts as "the package this line was counted in": the newest raw
 * quantity entry that names a vendor product. The entries are the auditable
 * record of what was physically counted; a line counted in the item's own
 * unit has no package entry and stamps nothing — counting loose pounds is not
 * evidence about packages. A line with mixed-package entries (one old Sysco
 * case + two loose bags) stamps the newest package entry, because that is the
 * package the counter reached for last.
 *
 * Non-fatal by design: a database without the migration, a missing template,
 * or any read/write failure logs a warning and leaves the count completion
 * untouched. Never throws.
 */

// PostgREST/Postgres "column not deployed yet" signatures.
const isMissingColumnError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return ['PGRST204', 'PGRST205', '42703'].includes(code)
    || /column .* does not exist|schema cache/i.test(message);
};

// Mirror the template editor's default so the (template, item, location)
// identity matches the row the editor writes.
const normalizeLocation = (value) =>
  value && String(value).trim() !== '' ? String(value) : 'No Location';

const entryInstant = (entry) => {
  const ms = Date.parse(entry?.created_at || '');
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Stamps the last-used count package onto the session's template rows.
 *
 * @param {object} supabase - client (defaults to the shared singleton)
 * @param {object|number|string} sessionOrId - count_sessions row or id
 * @param {{ completedAt?: string }} [opts]
 * @returns {Promise<{ stamped: number, skipped: string|null }>}
 */
export async function persistLastUsedCountFormats(supabase, sessionOrId, opts = {}) {
  const client = supabase || sharedSupabase;
  const none = (reason) => ({ stamped: 0, skipped: reason });
  try {
    const sessionId = typeof sessionOrId === 'object' && sessionOrId !== null
      ? sessionOrId.id
      : sessionOrId;
    if (!sessionId) return none('no_session');

    const sessionCarriesTemplate =
      typeof sessionOrId === 'object' && sessionOrId !== null && 'template_id' in sessionOrId;
    let templateId = sessionCarriesTemplate ? sessionOrId.template_id ?? null : null;
    if (!templateId && !sessionCarriesTemplate) {
      const { data: session, error } = await client
        .from('count_sessions')
        .select('id, template_id')
        .eq('id', sessionId)
        .maybeSingle();
      if (error || !session) return none('session_unreadable');
      templateId = session.template_id ?? null;
    }
    // An ad-hoc count belongs to no template; there is no row to remember on.
    if (!templateId) return none('no_template');

    const { data: countRows, error: rowsError } = await client
      .from('count_items')
      .select('id, inventory_item_id, location, is_counted')
      .eq('count_session_id', sessionId);
    if (rowsError) return none('count_items_unreadable');
    const counted = (countRows || []).filter((r) => r?.is_counted && r.inventory_item_id != null);
    if (counted.length === 0) return none('nothing_counted');

    const { data: entries, error: entriesError } = await client
      .from('count_item_quantity_entries')
      .select('count_item_id, purchase_option_id, created_at, sort_order')
      .eq('count_session_id', sessionId)
      .not('purchase_option_id', 'is', null);
    if (entriesError) {
      // The entries table arrives with migration 20260730120000. Without it
      // nothing was counted in a package, so there is nothing to remember.
      if (isMissingColumnError(entriesError)) return none('entries_unavailable');
      return none('entries_unreadable');
    }
    if (!entries || entries.length === 0) return none('no_package_entries');

    const rowById = new Map(counted.map((r) => [String(r.id), r]));
    // Newest package entry per (item, location) — across sibling rows too,
    // since the template identity is (template, item, location).
    const latestByKey = new Map();
    entries.forEach((entry) => {
      const row = rowById.get(String(entry.count_item_id));
      if (!row) return;
      const key = `${row.inventory_item_id}::${normalizeLocation(row.location)}`;
      const current = latestByKey.get(key);
      if (
        !current
        || entryInstant(entry) > entryInstant(current.entry)
        || (entryInstant(entry) === entryInstant(current.entry)
          && Number(entry.sort_order || 0) > Number(current.entry.sort_order || 0))
      ) {
        latestByKey.set(key, { entry, row });
      }
    });
    if (latestByKey.size === 0) return none('no_matching_entries');

    const usedAt = opts.completedAt || new Date().toISOString();
    let stamped = 0;
    let columnsMissing = false;

    // One update per (item, location) — a completion touches at most the
    // number of package-counted lines, and the write is fire-and-forget.
    for (const { entry, row } of latestByKey.values()) {
      const { error: updateError } = await client
        .from('count_template_items')
        .update({
          count_last_used_purchase_option_id: entry.purchase_option_id,
          count_last_used_at: usedAt,
        })
        .eq('template_id', templateId)
        .eq('inventory_item_id', row.inventory_item_id)
        .eq('location', normalizeLocation(row.location));
      if (updateError) {
        if (isMissingColumnError(updateError)) { columnsMissing = true; break; }
        console.warn('[count-format-memory] last-used stamp failed:', updateError.message);
      } else {
        stamped += 1;
      }
    }
    if (columnsMissing) {
      console.warn('[count-format-memory] count_last_used columns unavailable (migration not deployed?)');
      return { stamped, skipped: 'columns_unavailable' };
    }
    return { stamped, skipped: null };
  } catch (err) {
    console.warn('[count-format-memory] persistLastUsedCountFormats threw:', err);
    return none('threw');
  }
}

/**
 * The template's count-format memory per (item, location), shaped for
 * merging into the resolver's row input:
 * { [`${inventoryItemId}::${location}`]: {
 *     count_last_used_purchase_option_id, count_last_used_at,
 *     count_display_purchase_option_id, count_display_unit,
 *     follow_latest_purchase, template_item_id } }.
 *
 * Carries BOTH memories a template row holds: the last-used package (stamped
 * by persistLastUsedCountFormats) and the template-level pin (written by the
 * template editor's count-format control). Session creation does not copy
 * the format columns onto count rows, so without this merge a pin made in
 * the template editor would never reach a count.
 *
 * Returns {} on any failure — the resolver then simply skips those rungs,
 * exactly as for a store that has never used them.
 */
export async function fetchLastUsedCountFormats(supabase, templateId) {
  const client = supabase || sharedSupabase;
  if (!templateId) return {};
  const run = (columns) => client
    .from('count_template_items')
    .select(columns)
    .eq('template_id', templateId);
  try {
    let { data, error } = await run(
      'id, inventory_item_id, location, count_last_used_purchase_option_id, count_last_used_at, '
      + 'count_display_purchase_option_id, count_display_unit, follow_latest_purchase',
    );
    if (error && isMissingColumnError(error)) {
      // A database with the format columns but not the last-used pair (or
      // vice versa) still contributes what it has.
      ({ data, error } = await run(
        'id, inventory_item_id, location, count_display_purchase_option_id, count_display_unit, follow_latest_purchase',
      ));
    }
    if (error) {
      if (!isMissingColumnError(error)) {
        console.warn('[count-format-memory] template format fetch failed:', error.message);
      }
      return {};
    }
    const byKey = {};
    (data || []).forEach((row) => {
      if (row?.inventory_item_id == null) return;
      const hasMemory = row.count_last_used_purchase_option_id != null
        || row.count_display_purchase_option_id != null
        || row.follow_latest_purchase === false;
      if (!hasMemory) return;
      byKey[`${row.inventory_item_id}::${normalizeLocation(row.location)}`] = {
        template_item_id: row.id ?? null,
        count_last_used_purchase_option_id: row.count_last_used_purchase_option_id ?? null,
        count_last_used_at: row.count_last_used_at ?? null,
        count_display_purchase_option_id: row.count_display_purchase_option_id ?? null,
        count_display_unit: row.count_display_unit ?? null,
        follow_latest_purchase: row.follow_latest_purchase !== false,
      };
    });
    return byKey;
  } catch (err) {
    console.warn('[count-format-memory] template format fetch threw:', err);
    return {};
  }
}

export const lastUsedKeyFor = (inventoryItemId, location) =>
  `${inventoryItemId}::${normalizeLocation(location)}`;

export default persistLastUsedCountFormats;
